import { z } from "zod";

import type { LimitPayerType } from "@/shared/services/payments/paymentCorridors.service";
import { compareDecimalStrings, MONEY_INPUT_PATTERN, trimTrailingZeros } from "@/shared/utils/decimal";

import type {
  AmountLimitCell,
  AmountLimitRow,
  UpsertAmountLimitPayload,
} from "../../types/paymentLimits.types";

export const LIMIT_PAYER_CHOICES = ["BOTH", "PERSON", "COMPANY"] as const;
export type LimitPayerChoice = (typeof LIMIT_PAYER_CHOICES)[number];

export interface LimitFormMessages {
  money: string;
  atLeastOne: string;
  minBelowMax: string;
  fractionDigits: string;
}

const fractionDigitsOf = (value: string): number => value.split(".")[1]?.length ?? 0;

/**
 * Amounts stay decimal STRINGS (`MONEY_INPUT_PATTERN`), never `z.number()` — a number would round-trip an
 * 8-dp figure through a float. The currency precision is enforced here so a COP limit with cents is caught
 * before the backend's `invalid_amount_precision` would.
 */
export function buildLimitFormSchema(messages: LimitFormMessages, maxFractionDigits: number) {
  const money = z
    .string()
    .trim()
    .refine((value) => value === "" || MONEY_INPUT_PATTERN.test(value), messages.money)
    // Only a well-formed amount is measured — a malformed one already carries the pattern message.
    .refine(
      (value) =>
        value === "" || !MONEY_INPUT_PATTERN.test(value) || fractionDigitsOf(value) <= maxFractionDigits,
      messages.fractionDigits,
    );

  return z
    .object({
      payerType: z.enum(LIMIT_PAYER_CHOICES),
      minAmount: money,
      maxAmount: money,
      reason: z.string().trim().max(500),
    })
    .superRefine((values, ctx) => {
      if (!values.minAmount && !values.maxAmount) {
        ctx.addIssue({ code: "custom", path: ["maxAmount"], message: messages.atLeastOne });
        return;
      }
      if (
        values.minAmount &&
        values.maxAmount &&
        MONEY_INPUT_PATTERN.test(values.minAmount) &&
        MONEY_INPUT_PATTERN.test(values.maxAmount) &&
        compareDecimalStrings(values.minAmount, values.maxAmount) >= 0
      ) {
        ctx.addIssue({ code: "custom", path: ["minAmount"], message: messages.minBelowMax });
      }
    });
}

export type LimitFormValues = z.input<ReturnType<typeof buildLimitFormSchema>>;

export const payerChoiceOf = (payerType: LimitPayerType | null): LimitPayerChoice => payerType ?? "BOTH";

export const payerTypeOf = (choice: LimitPayerChoice): LimitPayerType | null =>
  choice === "BOTH" ? null : choice;

/** Form values seeded from the row being edited — an unbounded side is an empty field. */
export function seedLimitForm(row: AmountLimitRow | null, payerType: LimitPayerType | null): LimitFormValues {
  return {
    payerType: payerChoiceOf(row?.payer_type ?? payerType),
    minAmount: row?.min_amount ? trimTrailingZeros(row.min_amount) : "",
    maxAmount: row?.max_amount ? trimTrailingZeros(row.max_amount) : "",
    reason: row?.reason ?? "",
  };
}

export function toUpsertPayload(
  values: LimitFormValues,
  cell: Pick<AmountLimitCell, "direction" | "country" | "currency">,
  organizationId: string | null,
): UpsertAmountLimitPayload {
  const reason = values.reason.trim();
  return {
    direction: cell.direction,
    country: cell.country,
    currency: cell.currency,
    payer_type: payerTypeOf(values.payerType),
    organization_id: organizationId,
    min_amount: values.minAmount.trim() || null,
    max_amount: values.maxAmount.trim() || null,
    ...(reason ? { reason } : {}),
  };
}
